import { useQuery } from "@tanstack/react-query";
import { FC, ReactNode } from "react";
import { DateTime } from "luxon";
import styled from "styled-components";

import { api } from "@/utils/api";
import { Table, TableContent, Td, Th } from "@/components/Table";
import Text from "@/components/Text";
import { StyledScrollableTable, TableHolder } from "./ScoreByWins";

const StyledTable = styled(Table)`
  th,
  td {
    font-weight: 700;
    white-space: nowrap;
    &:first-child {
      min-width: 200px;
      padding-left: 24px;
      white-space: normal;
      min-width: 200px;
      padding-left: 24px;
      width: 200px;
      position: sticky;
      z-index: 1;
      background-color: ${({ theme }) => theme.colors.white};
      left: 0;
      top: 0;
      bottom: 0;
    }
    &:last-child {
      padding-right: 24px;
    }
  }
  td {
    padding: 16px;
  }
`;

const Wrap = styled.div`
  margin-bottom: 56px;
`;

const TableHeader = styled.div`
  padding: 24px;
`;

const Heading = styled(Text)`
  span {
    font-weight: 400;
  }
`;

const StyledText = styled(Text)`
  margin-bottom: 4px;
  &:last-child {
    margin-bottom: 0;
  }
  span {
    display: inline-block;
    margin-left: 3px;
    color: ${({ theme }) => theme.colors.black};
  }
`;

interface Participant {
  name: string;
  user_id: number;
  stats: {
    [date: string]: {
      variations: number;
      projects: number;
    };
  };
  totals: {
    variations: number;
    projects: number;
  };
}

interface Props {
  activePeriod: string;
  startDt: DateTime;
  endDt: DateTime;
  renderThs: () => ReactNode[];
  sequenceValue: {
    value: number | "All";
    type: string;
  };
}

const ScoreByActivity: FC<Props> = ({
  activePeriod,
  startDt,
  endDt,
  renderThs,
  sequenceValue,
}) => {
  const { data, isLoading, isError } = useQuery(
    [
      "leaderboard_scores",
      { type: "activity", period: activePeriod, sequenceValue },
    ],
    () => {
      return api.get<never, Participant[]>(
        "/v1/gamification/thumbnails/score-by-activity",
        {
          params: {
            date_from: activePeriod,
            date_to: DateTime.fromFormat(activePeriod, "yyyy-LL-dd")
              .endOf("month")
              .toFormat("yyyy-LL-dd"),
            ...(sequenceValue?.value !== "All" && {
              ...(sequenceValue?.type === "C"
                ? { c_batch: sequenceValue.value }
                : { batch: sequenceValue.value }),
            }),
          },
        }
      );
    }
  );

  const renderTds = (stats: Participant["stats"]) => {
    const components: ReactNode[] = [];

    let days = 0;

    while (startDt.plus({ days }).toFormat("dd") <= endDt.toFormat("dd")) {
      components.push(
        <Td key={days}>
          <StyledText $type="smallHeadline" $color="gray">
            Projects:
            <span>
              {stats[startDt.plus({ days }).toFormat("yyyy-LL-dd")]?.projects ??
                "-"}
            </span>
            <br />
          </StyledText>
          <StyledText $type="smallHeadline" $color="gray">
            Variations:
            <span>
              {stats[startDt.plus({ days }).toFormat("yyyy-LL-dd")]
                ?.variations ?? "-"}
            </span>
            <br />
          </StyledText>
        </Td>
      );

      days++;

      if (days === parseInt(endDt.toFormat("dd"))) {
        break;
      }
    }

    return components;
  };

  return (
    <Wrap>
      <TableHolder>
        <TableHeader>
          <Heading $type="smallBold">
            Participants Score by Activity{" "}
            <span>
              · {sequenceValue.type}
              {sequenceValue.value} ·{" "}
              {DateTime.fromISO(activePeriod).toFormat("yyyy LLLL")}
            </span>
          </Heading>
        </TableHeader>
        <StyledScrollableTable>
          <StyledTable>
            <thead>
              <tr>
                <Th>Participant</Th>
                {renderThs()}
                <Th>Total:</Th>
              </tr>
            </thead>
            <tbody>
              <TableContent
                isLoading={isLoading}
                isError={isError}
                hasData={!!data?.length}
              >
                {data?.map((participant, index) => (
                  <tr key={index}>
                    <Td>{participant.name}</Td>
                    {renderTds(participant.stats)}
                    <Td>
                      <StyledText $type="smallHeadline" $color="gray">
                        Projects:
                        <span>{participant.totals?.projects ?? "-"}</span>
                        <br />
                      </StyledText>
                      <StyledText $type="smallHeadline" $color="gray">
                        Variations:
                        <span>{participant.totals?.variations ?? "-"}</span>
                        <br />
                      </StyledText>
                    </Td>
                  </tr>
                ))}
              </TableContent>
            </tbody>
          </StyledTable>
        </StyledScrollableTable>
      </TableHolder>
    </Wrap>
  );
};

export default ScoreByActivity;
